import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ActivitySquare,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cloud,
  Download,
  ExternalLink,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Check,
  XCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const TEAM_SELECTION_GRADES = [
  "SCA Women's",
  "SCA Women's 1st Grade",
  "SCA Women's 2nd Grade",
  "Kookaburra T20 Tier 1",
  "Kookaburra T20 Tier 2",
  "Kookaburra T20 Tier 3",
  "Kookaburra T20 Tier 4",
  "SCA 1st Grade",
  "SCA 2nd Grade",
  "SCA 3rd Grade",
  "SCA 4th Grade",
  "SCA 5th Grade",
  "SCA 6th Grade",
  "SCA 7th Grade",
];

/** Turn an internal sync-run kind ("season", "sweeper_seasons", …) into a
 *  human-friendly label with a matching subtle badge. */
function runKindLabel(kind) {
  switch (kind) {
    case "seasons":        return { text: "Seasons", tag: "manual" };
    case "season":         return { text: "Season",  tag: "manual" };
    case "sweeper_seasons": return { text: "Seasons", tag: "auto" };
    case "sweeper_season":  return { text: "Season",  tag: "auto" };
    default:               return { text: kind || "run", tag: null };
  }
}

/** Format an ISO date as "9 Jul 2026, 6:14 pm". */
function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * StatChip — small pill used inside season cards to show a count. Uses a
 * subtle background rather than a Badge so the numbers read as data, not
 * status.
 */
function StatChip({ label, value, testid }) {
  return (
    <div className="flex flex-col items-start rounded-md bg-[hsl(var(--secondary))] px-3 py-2" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold tracking-tight tabular-nums">{value ?? 0}</span>
    </div>
  );
}

/** StatusPill — colour-coded season status. */
function StatusPill({ status }) {
  const map = {
    UPCOMING: "bg-blue-500/10 text-blue-600 border-blue-500/30",
    IN_PROGRESS: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    COMPLETED: "bg-muted text-muted-foreground border-border",
  };
  const cls = map[status] || "bg-muted text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {status || "UNKNOWN"}
    </span>
  );
}

export default function AdminPlayHQ() {
  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------
  const [status, setStatus] = useState(null); // /playhq/status payload
  const [statusLoading, setStatusLoading] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [counts, setCounts] = useState({}); // {season_id: {grades, teams, fixtures}}
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // busy flags — one per action so we can show individual spinners
  const [busySeasons, setBusySeasons] = useState(false);
  const [busySyncAll, setBusySyncAll] = useState(false);
  const [busySeason, setBusySeason] = useState(null); // season_id being synced
  const [confirmSyncAll, setConfirmSyncAll] = useState(false);

  // PlayHQ Game Day Link
  const [gamedayUrl, setGamedayUrl] = useState("");
  const [gamedayDraft, setGamedayDraft] = useState("");
  const [gamedayEditing, setGamedayEditing] = useState(false);
  const [gamedaySaving, setGamedaySaving] = useState(false);
  const [gamedaySaved, setGamedaySaved] = useState(false);

  // Team Selection Links — per-grade URLs
  const [selectionLinksOpen, setSelectionLinksOpen] = useState(false);
  const [selectionLinks, setSelectionLinks] = useState({});
  const [selectionDrafts, setSelectionDrafts] = useState({});
  const [selectionEditing, setSelectionEditing] = useState(null);
  const [selectionSaving, setSelectionSaving] = useState(null);
  const [selectionSaved, setSelectionSaved] = useState(null);
  const [customGrades, setCustomGrades] = useState([]);           // admin-added grades
  const [newGradeName, setNewGradeName] = useState("");
  const [gradesOrder, setGradesOrder] = useState(null);           // saved display order
  const [dragIdx, setDragIdx] = useState(null);                   // index being dragged
  const [dragOverIdx, setDragOverIdx] = useState(null);           // index being hovered

  // ------------------------------------------------------------------
  // Data loaders
  // ------------------------------------------------------------------
  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await api.get("/playhq/status");
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load PlayHQ status");
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadSeasons = useCallback(async () => {
    setSeasonsLoading(true);
    try {
      const r = await api.get("/playhq/seasons");
      const arr = Array.isArray(r.data) ? r.data : [];
      setSeasons(arr);
      // Load per-season counts in parallel — small requests, worth the UX.
      const results = await Promise.allSettled(
        arr.map(async (s) => {
          const sid = s.playhq_id;
          const [gRes, tRes] = await Promise.all([
            api.get(`/playhq/seasons/${sid}/grades`).catch(() => ({ data: [] })),
            api.get(`/playhq/seasons/${sid}/teams`).catch(() => ({ data: [] })),
          ]);
          const grades = Array.isArray(gRes.data) ? gRes.data : [];
          const teams = Array.isArray(tRes.data) ? tRes.data : [];
          // Fixtures live per-grade; count by summing grade fixture pages.
          const fxCounts = await Promise.all(
            grades.map((g) =>
              api
                .get(`/playhq/grades/${g.playhq_id}/fixtures`)
                .then((rr) => (Array.isArray(rr.data) ? rr.data.length : 0))
                .catch(() => 0)
            )
          );
          return {
            sid,
            counts: {
              grades: grades.length,
              teams: teams.length,
              fixtures: fxCounts.reduce((a, b) => a + b, 0),
            },
          };
        })
      );
      const nextCounts = {};
      for (const r2 of results) {
        if (r2.status === "fulfilled") nextCounts[r2.value.sid] = r2.value.counts;
      }
      setCounts(nextCounts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load PlayHQ seasons");
    } finally {
      setSeasonsLoading(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const r = await api.get("/playhq/sync/runs");
      setRuns(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sync history");
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadSeasons();
    loadRuns();
    // Load game day link
    api.get("/settings/gameday-link").then(({ data }) => {
      setGamedayUrl(data.url || "");
      setGamedayDraft(data.url || "");
    }).catch(() => {});
    // Load team selection links
    api.get("/settings/selection-links").then(({ data }) => {
      const links = data.links || {};
      setSelectionLinks(links);
      setSelectionDrafts({ ...links });
      setCustomGrades(data.custom_grades || []);
      if (data.grades_order) setGradesOrder(data.grades_order);
    }).catch(() => {});
  }, [loadStatus, loadSeasons, loadRuns]);

  const saveGamedayLink = async () => {
    setGamedaySaving(true);
    try {
      const { data } = await api.put("/settings/gameday-link", { url: gamedayDraft.trim() });
      setGamedayUrl(data.url || "");
      setGamedayEditing(false);
      setGamedaySaved(true);
      setTimeout(() => setGamedaySaved(false), 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save link");
    }
    setGamedaySaving(false);
  };

  const saveSelectionLink = async (grade) => {
    setSelectionSaving(grade);
    try {
      const url = (selectionDrafts[grade] || "").trim();
      const { data } = await api.put("/settings/selection-links", { grade, url });
      setSelectionLinks(data.links || {});
      setSelectionEditing(null);
      setSelectionSaved(grade);
      setTimeout(() => setSelectionSaved(null), 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save link");
    }
    setSelectionSaving(null);
  };

  // Combine default + custom grades (deduped), then apply saved order
  const _unordered = [...TEAM_SELECTION_GRADES, ...customGrades.filter((g) => !TEAM_SELECTION_GRADES.includes(g))];
  const allGrades = gradesOrder
    ? [...gradesOrder.filter((g) => _unordered.includes(g)), ..._unordered.filter((g) => !gradesOrder.includes(g))]
    : _unordered;

  const addGrade = async () => {
    const name = newGradeName.trim();
    if (!name) return;
    if (allGrades.includes(name)) {
      toast.error("That grade already exists");
      return;
    }
    try {
      const { data } = await api.post("/settings/selection-links/grades", { grade: name });
      setCustomGrades(data.custom_grades || []);
      setNewGradeName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add grade");
    }
  };

  const removeGrade = async (grade) => {
    try {
      const { data } = await api.delete("/settings/selection-links/grades", { data: { grade } });
      setCustomGrades(data.custom_grades || []);
      // Also clear saved link for that grade
      setSelectionLinks((prev) => { const n = { ...prev }; delete n[grade]; return n; });
      setSelectionDrafts((prev) => { const n = { ...prev }; delete n[grade]; return n; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove grade");
    }
  };

  // Drag-and-drop reorder
  const handleDragStart = (idx) => { setDragIdx(idx); };
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };
  const handleDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { handleDragEnd(); return; }
    const reordered = [...allGrades];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    setGradesOrder(reordered);
    handleDragEnd();
    // Persist the new order
    try {
      await api.put("/settings/selection-links/order", { order: reordered });
    } catch (e) {
      toast.error("Failed to save order");
    }
  };

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------
  const doSyncSeasons = async () => {
    setBusySeasons(true);
    try {
      const r = await api.post("/playhq/sync/seasons");
      const { fetched = 0, upserted = 0, modified = 0 } = r.data || {};
      toast.success(`Seasons synced · ${fetched} fetched, ${upserted} new, ${modified} updated`);
      await Promise.all([loadSeasons(), loadRuns()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusySeasons(false);
    }
  };

  const doSyncSeason = async (season) => {
    const sid = season.playhq_id;
    setBusySeason(sid);
    try {
      const r = await api.post(`/playhq/sync/season/${sid}`);
      const g = r.data?.grades?.fetched ?? 0;
      const t = r.data?.teams?.fetched ?? 0;
      const f = r.data?.fixtures?.fetched ?? 0;
      const l = r.data?.ladders?.ok ?? 0;
      toast.success(`${season.name} synced · ${g} grades · ${t} teams · ${f} fixtures · ${l} ladders`);
      await Promise.all([loadSeasons(), loadRuns()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Season sync failed");
    } finally {
      setBusySeason(null);
    }
  };

  const doSyncAll = async () => {
    setConfirmSyncAll(false);
    setBusySyncAll(true);
    try {
      const r = await api.post("/playhq/sync/all");
      const perSeason = Array.isArray(r.data?.per_season) ? r.data.per_season : [];
      const errs = perSeason.filter((p) => p.error);
      if (errs.length) {
        toast.warning(`Sync completed with ${errs.length} season error${errs.length === 1 ? "" : "s"}`);
      } else {
        toast.success(`Full sync complete · ${perSeason.length} season${perSeason.length === 1 ? "" : "s"}`);
      }
      await Promise.all([loadStatus(), loadSeasons(), loadRuns()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Full sync failed");
    } finally {
      setBusySyncAll(false);
    }
  };

  // ------------------------------------------------------------------
  // Derived
  // ------------------------------------------------------------------
  const configured = !!status?.configured;
  const reachable = !!status?.reachable;
  const activeCount = useMemo(
    () => seasons.filter((s) => s.status !== "COMPLETED").length,
    [seasons]
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <AppShell
      variant="admin"
      title="PlayHQ"
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() => setConfirmSyncAll(true)}
          disabled={busySyncAll || !configured || !reachable}
          className="rounded-full"
          data-testid="playhq-sync-all-open"
        >
          {busySyncAll ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Cloud className="h-3.5 w-3.5 mr-1.5" />}
          Sync everything
        </Button>
      }
    >
      <div className="space-y-6">
        {/* ============ Config / connectivity ============ */}
        <Card className="p-4 sm:p-5 rounded-2xl" data-testid="playhq-config-card">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
              statusLoading
                ? "bg-muted text-muted-foreground"
                : configured && reachable
                  ? "bg-emerald-500/10 text-emerald-700"
                  : "bg-red-500/10 text-red-600"
            }`}>
              {statusLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : configured && reachable ? (
                <ShieldCheck className="h-5 w-5" />
              ) : (
                <AlertTriangle className="h-5 w-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold tracking-tight">Connection</h2>
                {!statusLoading && (
                  configured && reachable ? (
                    <Badge variant="outline" className="text-emerald-700 border-emerald-500/40 bg-emerald-500/5" data-testid="playhq-connected-badge">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-red-600 border-red-500/40 bg-red-500/5" data-testid="playhq-disconnected-badge">
                      <XCircle className="h-3 w-3 mr-1" /> {configured ? "Unreachable" : "Not configured"}
                    </Badge>
                  )
                )}
              </div>
              <div className="mt-2 grid gap-1 text-sm text-muted-foreground">
                <div><span className="text-foreground/70">Base URL:</span> <span className="font-mono text-xs" data-testid="playhq-base-url">{status?.base_url || "—"}</span></div>
                <div><span className="text-foreground/70">Tenant:</span> <span className="font-mono text-xs" data-testid="playhq-tenant">{status?.tenant || "—"}</span></div>
                <div className="truncate"><span className="text-foreground/70">Organisation ID:</span> <span className="font-mono text-xs" data-testid="playhq-org-id">{status?.org_id || "—"}</span></div>
                {status?.error && (
                  <div className="text-red-600" data-testid="playhq-error">Error: {status.error}</div>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadStatus}
              disabled={statusLoading}
              className="rounded-full"
              data-testid="playhq-refresh-status"
              title="Re-check connectivity"
            >
              {statusLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </Card>

        {/* ============ Team Selection Links (collapsible) ============ */}
        <Collapsible open={selectionLinksOpen} onOpenChange={setSelectionLinksOpen}>
          <Card className="rounded-2xl overflow-hidden" data-testid="team-selection-links-card">
            <CollapsibleTrigger asChild>
              <button className="w-full p-4 sm:p-5 flex items-center gap-3 sm:gap-4 hover:bg-[hsl(var(--secondary))]/40 transition-colors text-left">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 bg-[#1a3a7a]/10 text-[#1a3a7a]">
                  <ExternalLink className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold tracking-tight">Team Selection Links</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {Object.values(selectionLinks).filter(Boolean).length} of {allGrades.length} links set
                  </p>
                </div>
                <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${selectionLinksOpen ? "rotate-180" : ""}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border-t border-border divide-y divide-border">
                {allGrades.map((grade, gi) => {
                  const isCustom = customGrades.includes(grade);
                  const savedUrl = selectionLinks[grade] || "";
                  const isEditing = selectionEditing === grade;
                  const isSaving = selectionSaving === grade;
                  const justSaved = selectionSaved === grade;
                  const isDragging = dragIdx === gi;
                  const isDragOver = dragOverIdx === gi;
                  return (
                    <div
                      key={grade}
                      draggable
                      onDragStart={() => handleDragStart(gi)}
                      onDragOver={(e) => handleDragOver(e, gi)}
                      onDragEnd={handleDragEnd}
                      onDrop={() => handleDrop(gi)}
                      className={`flex items-center gap-2 px-4 sm:px-5 py-3 transition-colors cursor-grab active:cursor-grabbing ${isDragging ? "opacity-40" : ""} ${isDragOver && dragIdx !== null ? "bg-[#1a3a7a]/5 border-t-2 border-t-[#1a3a7a]/40" : "hover:bg-[hsl(var(--secondary))]/20"}`}
                      data-testid={`selection-link-${grade.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground/50 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{grade}</div>
                        {!isEditing ? (
                          <div className="mt-0.5">
                            {savedUrl ? (
                              <a href={savedUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-[#1a3a7a] truncate block max-w-sm underline underline-offset-2 transition-colors">{savedUrl}</a>
                            ) : (
                              <span className="text-xs text-muted-foreground italic">No link set</span>
                            )}
                          </div>
                        ) : (
                          <Input
                            type="url"
                            value={selectionDrafts[grade] || ""}
                            onChange={(e) => setSelectionDrafts((d) => ({ ...d, [grade]: e.target.value }))}
                            placeholder="https://www.playhq.com/..."
                            className="h-8 text-xs mt-1"
                            onKeyDown={(e) => { if (e.key === "Enter") saveSelectionLink(grade); if (e.key === "Escape") { setSelectionEditing(null); setSelectionDrafts((d) => ({ ...d, [grade]: savedUrl })); } }}
                            autoFocus
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {isEditing ? (
                          <>
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setSelectionEditing(null); setSelectionDrafts((d) => ({ ...d, [grade]: savedUrl })); }}>Cancel</Button>
                            <Button size="sm" className="h-7 px-2.5 text-xs bg-[#1a3a7a] hover:bg-[#1a3a7a]/90 text-white" onClick={() => saveSelectionLink(grade)} disabled={isSaving}>
                              {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="h-3 w-3 mr-1" /> Save</>}
                            </Button>
                          </>
                        ) : (
                          <>
                            {justSaved && <Check className="h-3.5 w-3.5 text-emerald-500" />}
                            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={() => setSelectionEditing(grade)}>
                              <Pencil className="h-3 w-3 mr-1" /> {savedUrl ? "Edit" : "Add"}
                            </Button>
                            {savedUrl && (
                              <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={() => window.open(savedUrl, "_blank")}>
                                <ExternalLink className="h-3 w-3 mr-1" /> Open
                              </Button>
                            )}
                            {isCustom && (
                              <Button variant="ghost" size="sm" className="h-7 px-1.5 text-xs text-muted-foreground hover:text-destructive" onClick={() => removeGrade(grade)} data-testid={`remove-grade-${grade.replace(/\s+/g, "-").toLowerCase()}`}>
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Add Grade row */}
                <div className="flex items-center gap-2 px-4 sm:px-5 py-3">
                  <Input
                    type="text"
                    value={newGradeName}
                    onChange={(e) => setNewGradeName(e.target.value)}
                    placeholder="New grade name…"
                    className="h-8 text-xs flex-1"
                    data-testid="add-grade-input"
                    onKeyDown={(e) => { if (e.key === "Enter") addGrade(); }}
                  />
                  <Button size="sm" variant="outline" className="h-8 px-3 text-xs" onClick={addGrade} disabled={!newGradeName.trim()} data-testid="add-grade-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Grade
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* ============ PlayHQ Admin ============ */}
        <Card className="p-4 sm:p-5 rounded-2xl" data-testid="playhq-admin-card">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 bg-[#1a3a7a]/10 text-[#1a3a7a]">
              <Cloud className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold tracking-tight">PlayHQ Admin</h2>
              <p className="text-sm text-muted-foreground mt-1">Manage PlayHQ admin settings and configurations.</p>
            </div>
          </div>
        </Card>

        {/* ============ Seasons list (hidden per admin preference) ============ */}
        {false && (
        <Card className="p-4 sm:p-5 rounded-2xl" data-testid="playhq-seasons-card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Cached seasons</div>
              <h2 className="text-lg font-semibold tracking-tight" data-testid="playhq-seasons-heading">
                {seasons.length} season{seasons.length === 1 ? "" : "s"}
                {activeCount > 0 && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground">· {activeCount} active</span>
                )}
              </h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={doSyncSeasons}
              disabled={busySeasons || !configured || !reachable}
              className="rounded-full"
              data-testid="playhq-sync-seasons"
            >
              {busySeasons ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Refresh seasons
            </Button>
          </div>

          {seasonsLoading && seasons.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading seasons…
            </div>
          ) : seasons.length === 0 ? (
            <div className="py-10 text-center border border-dashed border-border rounded-xl">
              <div className="text-sm font-semibold">No seasons cached yet</div>
              <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
                Click <span className="font-medium">Refresh seasons</span> above to pull the list from PlayHQ. Then sync each season individually.
              </p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="playhq-seasons-list">
              {seasons.map((s) => {
                const c = counts[s.playhq_id] || {};
                const isBusy = busySeason === s.playhq_id;
                return (
                  <li
                    key={s.playhq_id}
                    className="rounded-xl border border-border p-3 sm:p-4 hover:border-foreground/25 transition-colors"
                    data-testid={`playhq-season-row-${s.playhq_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold tracking-tight" data-testid={`playhq-season-name-${s.playhq_id}`}>{s.name}</h3>
                          <StatusPill status={s.status} />
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {s.association_name || "—"}
                          {s.competition_name ? ` · ${s.competition_name}` : ""}
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-2 max-w-md">
                          <StatChip label="Grades" value={c.grades} testid={`playhq-season-grades-${s.playhq_id}`} />
                          <StatChip label="Teams" value={c.teams} testid={`playhq-season-teams-${s.playhq_id}`} />
                          <StatChip label="Fixtures" value={c.fixtures} testid={`playhq-season-fixtures-${s.playhq_id}`} />
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => doSyncSeason(s)}
                        disabled={isBusy || !configured || !reachable}
                        className="rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--bg))] hover:bg-[hsl(var(--fg))]/90 shrink-0"
                        data-testid={`playhq-sync-season-${s.playhq_id}`}
                      >
                        {isBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                        {isBusy ? "Syncing…" : "Sync season"}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        )}

      </div>

      {/* Confirm dialog for the "Sync everything" button — it takes a while
          and hits the PlayHQ API a lot, so make the admin confirm. */}
      <AlertDialog open={confirmSyncAll} onOpenChange={setConfirmSyncAll}>
        <AlertDialogContent data-testid="playhq-sync-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ActivitySquare className="h-5 w-5 text-[hsl(var(--accent))]" /> Sync every season?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will pull every season, then every grade, team, fixture and ladder for each season from
              PlayHQ. It can take a couple of minutes on the first run. You can safely keep working — the sync
              runs on the server.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="playhq-sync-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSyncAll} data-testid="playhq-sync-all-confirm">Sync everything</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
